import React, { useMemo } from 'react';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LoadingComponent from './loading';
import useJobPolling from './hooks/useJobPolling';

const API_BASE = 'https://api.insightos.co.uk';

function SummaryRow({ label, value, tone = 'dark' }) {
  const className = tone === 'light'
    ? 'min-w-0 rounded-xl border border-white/80 bg-white/80 px-3 py-3 shadow-[0_10px_28px_rgba(37,99,235,0.1)] sm:px-4'
    : 'min-w-0 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 sm:px-4';
  const labelClassName = tone === 'light'
    ? 'text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500'
    : 'text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400';
  const valueClassName = tone === 'light'
    ? 'mt-1 break-words text-base font-semibold text-slate-900 sm:text-lg'
    : 'mt-1 break-words text-base font-semibold text-white sm:text-lg';

  return (
    <div className={className}>
      <p className={labelClassName}>{label}</p>
      <p className={valueClassName}>{value}</p>
    </div>
  );
}

export default function AvenorJobProgress({ queueType = 'leads' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  const isAdvertQueue = queueType === 'advert';
  const {
    title = isAdvertQueue ? 'Generating advert' : 'Processing Avenor job',
    backPath = isAdvertQueue ? '/gallery' : '/leads',
    returnLabel = isAdvertQueue ? 'Return to Gallery' : 'Return to Leads',
    locationFilter = '',
    sectorFilter = '',
    companySearch = '',
  } = location.state || {};
  const resolvedBackPath = isAdvertQueue && (backPath === '/avenor' || backPath === '/galley')
    ? '/gallery'
    : backPath;

  const statusUrl = isAdvertQueue ? `/api/advertjobs/${jobId}` : `/api/leadsjobs/${jobId}`;
  const { error, job, progress, progressDetails, stage, status, isComplete, isFailed } = useJobPolling(jobId, {
    enabled: Boolean(jobId),
    statusUrl,
  });

  const recentLogs = Array.isArray(job?.logs) ? job.logs.slice(-8) : [];
  const result = job?.result || null;
  const queue = progressDetails?.queue || job?.queue || null;
  const currentLead = progressDetails?.currentLead || null;
  const finishedLeads = Array.isArray(progressDetails?.finishedLeads) ? progressDetails.finishedLeads : [];
  const jobLeads = Array.isArray(progressDetails?.jobLeads) ? progressDetails.jobLeads : [];
  const processedCount = Number(progressDetails?.processedCount ?? 0) || 0;
  const enrichedCount = Number(progressDetails?.enrichedCount ?? 0) || 0;
  const totalLeads = Number(progressDetails?.totalLeads ?? 0) || 0;
  const videoUrl = result?.videoUrl
    ? (String(result.videoUrl).startsWith('http') ? result.videoUrl : `${API_BASE}${result.videoUrl}`)
    : '';
  const downloadUrl = result?.downloadUrl
    ? (String(result.downloadUrl).startsWith('http') ? result.downloadUrl : `${API_BASE}${result.downloadUrl}`)
    : '';

  const leadSummary = useMemo(() => {
    if (!result || isAdvertQueue) {
      return [];
    }

    if (typeof result.total === 'number') {
      return [
        { label: 'Leads Found', value: result.total },
        { label: 'Inserted', value: result.insertedCount ?? 0 },
        { label: 'Skipped', value: result.skippedCount ?? 0 },
      ];
    }

    if (typeof result.processed === 'number') {
      return [
        { label: 'Processed', value: result.processed },
        { label: 'Enriched', value: result.enrichedCount ?? 0 },
        { label: 'Credits Spent', value: result.credits?.spent ?? 0 },
        { label: 'Remaining Credits', value: result.credits?.salesCredits ?? result.credits?.salesCredit ?? 0 },
      ];
    }

    if (typeof result.emailsSent === 'number') {
      return [
        { label: 'Emails Sent', value: result.emailsSent },
        { label: 'Stage', value: result.emailState?.emailStage ?? '-' },
        { label: 'Total Sent', value: result.emailState?.emailSent ?? '-' },
        { label: 'Remaining Today', value: result.emailState?.remainingToday ?? '-' },
      ];
    }

    return [];
  }, [isAdvertQueue, result]);

  const completedLeadRows = useMemo(() => {
    if (!Array.isArray(result?.reports)) {
      return [];
    }

    return result.reports.map((item, index) => ({
      key: `${item?.id || 'lead'}-${index}`,
      id: item?.id || null,
      company: item?.company || item?.report?.companyName || `Lead ${index + 1}`,
      success: Boolean(item?.success),
      reason: item?.reason || null,
      creditsUsed: Number(item?.creditsUsed || 0) || 0,
    }));
  }, [result]);

  const loadingDetails = !isAdvertQueue ? (
    <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(18rem,0.75fr)]">
      <div className="min-w-0 space-y-4">
        <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 2xl:grid-cols-4">
          <SummaryRow label="Queue State" value={queue?.state || status || 'waiting'} tone="light" />
          <SummaryRow label="Queue Position" value={queue?.queuePosition ? `#${queue.queuePosition}` : queue?.state === 'active' ? 'Active' : '-'} tone="light" />
          <SummaryRow label="Processed" value={totalLeads ? `${processedCount}/${totalLeads}` : processedCount} tone="light" />
          <SummaryRow label="Enriched" value={enrichedCount} tone="light" />
        </div>

        {currentLead ? (
          <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-left shadow-[0_12px_35px_rgba(37,99,235,0.12)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Current Lead</p>
            <p className="mt-2 text-base font-semibold text-slate-900">{currentLead.company || 'Unknown company'}</p>
            <p className="mt-1 break-words text-sm text-slate-600">Lead ID {currentLead.id || '-'}{currentLead.total ? ` • ${currentLead.index}/${currentLead.total}` : ''}</p>
          </div>
        ) : null}

        {finishedLeads.length > 0 ? (
          <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-left shadow-[0_12px_35px_rgba(37,99,235,0.12)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Finished Leads</p>
            <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
              {finishedLeads.slice().reverse().map((lead) => (
                <div key={`${lead.id}-${lead.status || 'done'}`} className="flex min-w-0 flex-col gap-2 rounded-lg border border-white/80 bg-white/75 px-3 py-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold text-slate-900">{lead.company || 'Unknown company'}</p>
                    <p className="break-words text-xs text-slate-500">Lead ID {lead.id || '-'}{lead.reason ? ` • ${lead.reason}` : ''}</p>
                  </div>
                  <span className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${lead.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : lead.status === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                    {lead.status || 'done'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {jobLeads.length > 0 ? (
        <div className="min-w-0 rounded-2xl border border-white/70 bg-white/70 p-4 text-left shadow-[0_12px_35px_rgba(37,99,235,0.12)]">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Leads In This Job</p>
          <div className="mt-3 max-h-[32rem] space-y-2 overflow-y-auto pr-1">
            {jobLeads.map((lead) => (
              <div key={`${lead.id}-${lead.company}`} className="rounded-xl border border-white/80 bg-white/75 px-3 py-2">
                <p className="break-words text-sm font-semibold text-slate-900">{lead.company || 'Unknown company'}</p>
                <p className="break-all text-xs text-slate-500">Lead ID {lead.id || '-'}{lead.website ? ` • ${lead.website}` : ''}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  const advertLoadingDetails = isAdvertQueue ? (
    <div className="rounded-2xl border border-fuchsia-300/60 bg-gradient-to-br from-fuchsia-100/70 via-cyan-100/60 to-amber-100/70 p-4 text-left shadow-[0_18px_45px_rgba(217,70,239,0.2)]">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-700">Render Pipeline</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <SummaryRow label="Stage" value={stage || status || 'preparing'} tone="light" />
        <SummaryRow label="Job" value={jobId ? `#${jobId}` : '-'} tone="light" />
        <SummaryRow label="Progress" value={`${Math.max(0, Math.min(100, Number(progress) || 0))}%`} tone="light" />
      </div>
    </div>
  ) : null;

  if (isFailed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-8 text-white sm:px-6">
        <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-white/5 p-5 shadow-2xl backdrop-blur-xl sm:p-8">
          <div className="mb-6 flex items-start gap-4">
            <div className="shrink-0 rounded-xl bg-red-500/20 p-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-300" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Job failed</h1>
              <p className="text-sm text-white/60">The queue worker reported an error for this Avenor request.</p>
            </div>
          </div>

          <div className="break-words whitespace-pre-wrap rounded-xl border border-white/10 bg-black/30 p-4 text-sm text-white/80">
            {error || job?.failedReason || 'An unknown error occurred.'}
          </div>

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => navigate(resolvedBackPath, { replace: true, state: { avenorRefreshKey: Date.now() } })}
              className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-4 py-2 font-medium hover:bg-slate-200 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isComplete) {
    return (
      <LoadingComponent
        label={title}
        subtitle={isAdvertQueue ? 'Preparing advert generation' : 'Tracking your queue job'}
        progress={progress}
        stage={stage || status}
        detail={jobId ? `Job ${jobId}` : 'Preparing job'}
        logs={recentLogs}
        fullBleed
      >
        {isAdvertQueue ? advertLoadingDetails : loadingDetails}
      </LoadingComponent>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden px-4 py-6 text-white sm:px-6 sm:py-8 ${isAdvertQueue ? 'bg-gradient-to-br from-[#210035] via-[#0b1a4a] to-[#132a13]' : 'bg-slate-950'}`}>
      <div className="mx-auto w-full max-w-7xl space-y-5">
        <header className={`rounded-2xl p-5 shadow-2xl sm:p-7 ${isAdvertQueue ? 'border border-fuchsia-300/35 bg-gradient-to-br from-fuchsia-500/25 via-sky-500/20 to-amber-400/20' : 'border border-cyan-400/20 bg-gradient-to-br from-cyan-500/15 via-slate-950 to-emerald-500/10'}`}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <p className={`text-[11px] font-semibold uppercase tracking-[0.2em] ${isAdvertQueue ? 'text-fuchsia-200' : 'text-cyan-300'}`}>Avenor Queue</p>
              <h1 className="mt-2 text-2xl font-semibold text-white sm:text-3xl">Job completed</h1>
              <p className={`mt-2 max-w-2xl text-sm ${isAdvertQueue ? 'text-fuchsia-100/90' : 'text-slate-300'}`}>
                {isAdvertQueue
                  ? 'Your advert generation finished. Review the output below or download the generated video.'
                  : 'Your queue job finished. Review the processed totals below before returning to Leads.'}
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={() => navigate(resolvedBackPath, { replace: true, state: { avenorRefreshKey: Date.now() } })}
                className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors ${isAdvertQueue ? 'bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-white hover:from-fuchsia-400 hover:to-cyan-300' : 'bg-white text-slate-950 hover:bg-slate-200'}`}
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {returnLabel}
              </button>
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  className={`inline-flex min-h-11 items-center justify-center rounded-lg border px-4 py-2 font-medium transition-colors ${isAdvertQueue ? 'border-amber-200/50 bg-amber-300/20 text-amber-100 hover:bg-amber-300/35' : 'border-cyan-300/40 bg-cyan-400/10 text-cyan-100 hover:bg-cyan-400/20'}`}
                >
                  Download Video
                </a>
              ) : null}
            </div>
          </div>
        </header>

        {isAdvertQueue && videoUrl ? (
          <section className="rounded-2xl border border-fuchsia-300/25 bg-gradient-to-br from-fuchsia-500/10 via-sky-400/10 to-amber-300/10 p-4 shadow-2xl backdrop-blur-xl sm:p-5">
            <p className="mb-3 text-sm font-semibold text-fuchsia-100">Advert Preview</p>
            <video controls className="aspect-video max-h-[70vh] w-full rounded-xl bg-black object-contain" src={videoUrl} />
            {result?.gallerySaved === false && result?.galleryError ? (
              <p className="mt-3 text-sm text-amber-200">Gallery save failed: {result.galleryError}</p>
            ) : null}
          </section>
        ) : null}

        {leadSummary.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 lg:grid-cols-4">
            {leadSummary.map((item) => (
              <SummaryRow key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        ) : null}

        {!isAdvertQueue && completedLeadRows.length > 0 ? (
          <section className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-2xl backdrop-blur-xl sm:p-5">
            <p className="mb-3 text-sm font-semibold text-cyan-200">Processed Leads</p>
            <div className="space-y-3">
              {completedLeadRows.map((lead) => (
                <div key={lead.key} className="flex min-w-0 flex-col gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-semibold text-white">{lead.company}</p>
                    <p className="mt-1 break-words text-xs text-slate-400">Lead ID {lead.id || '-'}{lead.reason ? ` • ${lead.reason}` : ''}</p>
                  </div>
                  <div className="shrink-0 text-left sm:text-right">
                    <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] ${lead.success ? 'bg-emerald-500/20 text-emerald-200' : 'bg-rose-500/20 text-rose-200'}`}>
                      {lead.success ? 'Completed' : 'Failed'}
                    </span>
                    <p className="mt-2 text-xs text-slate-400">Credits {lead.creditsUsed}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {Array.isArray(recentLogs) && recentLogs.length > 0 ? (
          <section className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-2xl backdrop-blur-xl sm:p-5">
            <p className="mb-3 text-sm font-semibold text-cyan-200">Recent Log</p>
            <div className="space-y-2 font-mono text-xs text-slate-200">
              {recentLogs.map((logLine, index) => (
                <p key={`${index}-${logLine}`} className="break-words">{logLine}</p>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}